import { createContext, type ReactNode, useContext } from "react";
import type {
  CreateTarget,
  VersionOrigin,
} from "@/features/standalone-versions";

export type FunnelMode = "signup" | "platform";

/** Payload handed to `onComplete` when the funnel finishes (platform mode). */
export interface FunnelCompletePayload {
  docType?: string;
  templateId?: string;
  /** Full template story JSON (fetched from its contentPointer) when a template was chosen. */
  templateStory?: Record<string, unknown>;
  brand?: string;
  /** Final styling chosen on the Ready step, applied to the created story. */
  palette?: { brand: string; accent: string; light: string };
  /**
   * Base design system from brand-ai-extract — the roles the AI assigned.
   * Overridden per-slot by the Ready-step picks below, so an untouched run
   * styles the story exactly as the AI intended.
   */
  designRoles?: { icons: string; background: string; title: string };
  /**
   * Full design-system overrides from the editor's Brand Import mapping —
   * the template path passes these so a picked brand repaints the whole
   * template (highlights, media overlays, secondary colours, every font
   * slot), not just the three wizard role slots. Role/pick fields below
   * still win per-slot where present.
   */
  brandDesign?: Record<string, string>;
  /**
   * Custom-color palette carried by a picked account theme — set onto the
   * created story's settings alongside the design system, as the theme
   * editor does.
   */
  customColors?: string[];
  /** Ready-step theme-row picks — override the palette-derived role colors. */
  iconsColor?: string;
  accentBackgroundColor?: string;
  titlesColor?: string;
  headlineFont?: string;
  subtitleFont?: string;
  bodyFont?: string;
  /**
   * Defaults to true: styling picked in the funnel is the user's explicit
   * choice, so the account theme must not overwrite it. Paths with no styling
   * step of their own (direct "Use template") pass false so the story picks up
   * the account design system — and falls back to the template's own when the
   * account has none.
   */
  keepTemplateDesignSystem?: boolean;
  /**
   * Where the document came from -- a template (named), saved slides, AI, a
   * blank page. Kept on a standalone version's record so the Versions page
   * can say so; a story shows its folder instead and ignores it.
   */
  origin?: VersionOrigin;
  /**
   * The org story the document was made from, when it is one. A version
   * made from it hangs off that story; without one it stands alone on the
   * user's host page. See features/standalone-versions.
   */
  basePageId?: string | null;
}

interface FunnelModeValue {
  mode: FunnelMode;
  /**
   * What the run ends in on the platform: a story (the default) or a
   * standalone version. Copy that names the thing being made reads this.
   */
  target?: CreateTarget;
  /** Platform: close the overlay (funnel X / exit). */
  onClose?: () => void;
  /** Platform "Start with slides" — hand off to the slides-library dialog. */
  onSlides?: () => void;
  /**
   * Platform: finish by creating a real story and opening it in the editor.
   * Awaitable so callers can keep a loading state up until the story exists.
   */
  /**
   * Whatever it resolves to is the completer's business -- the platform's
   * handler answers with the created story for the deck runner -- so callers
   * that only await it are not tied to its shape.
   */
  onComplete?: (payload: FunnelCompletePayload) => void | Promise<unknown>;
}

const FunnelModeContext = createContext<FunnelModeValue>({ mode: "signup" });

export const FunnelModeProvider = ({
  value,
  children,
}: {
  value: FunnelModeValue;
  children: ReactNode;
}) => (
  <FunnelModeContext.Provider value={value}>
    {children}
  </FunnelModeContext.Provider>
);

/** Funnel mode: `signup` (public) or `platform` (logged-in overlay, no brand/colors steps, real story creation). */
export const useFunnelMode = () => useContext(FunnelModeContext);
